import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendOTP, verifyOTP, sendEmailCode, verifyEmailCode } from '../services/firebase';
import { useLanguage } from '../context/LanguageContext';
import { useBrand } from '../context/BrandContext';
import BrandLogo from '../components/BrandLogo';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

// German mobile prefixes (after +49 normalization). Everything else = landline.
const GERMAN_MOBILE = ['15', '16', '17'];
function isMobile(raw: string): boolean {
  const p = (raw || '').replace(/[\s\-()]/g, '');
  if (p.startsWith('+') && !p.startsWith('+49')) return true;
  if (p.startsWith('00') && !p.startsWith('0049')) return true;
  const national = p.startsWith('+49') ? p.slice(3) : p.startsWith('0049') ? p.slice(4) : p.startsWith('0') ? p.slice(1) : p;
  return GERMAN_MOBILE.some(pre => national.startsWith(pre));
}

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang } = useLanguage();
  const { brand } = useBrand();

  // 'phone' = enter phone, 'email' = enter email (landline), 'otp' = enter code
  const [step, setStep] = useState<'phone' | 'email' | 'otp'>('phone');
  const [phone, setPhone] = useState<string | undefined>('');
  const [email, setEmail] = useState('');
  const [verifyChannel, setVerifyChannel] = useState<'phone' | 'email'>('phone');
  const [channelUsed, setChannelUsed] = useState<'sms' | 'whatsapp' | 'email'>('sms');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  // Track if the user entered a landline — shows the email option
  const [landlineDetected, setLandlineDetected] = useState(false);

  const otpRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];
  const returnTo = (location.state as any)?.from || '/';

  useEffect(() => { if (countdown <= 0) return; const t = setTimeout(() => setCountdown(c => c - 1), 1000); return () => clearTimeout(t); }, [countdown]);
  useEffect(() => { if (step === 'otp') setTimeout(() => otpRefs[0].current?.focus(), 100); }, [step]); // eslint-disable-line

  // Detect landline when phone changes
  useEffect(() => {
    if (phone && phone.length >= 8) {
      setLandlineDetected(!isMobile(phone));
    } else {
      setLandlineDetected(false);
    }
  }, [phone]);

  // ─── Phone OTP ───
  const handleSendPhoneOTP = async () => {
    if (!phone || phone.length < 8) { setError(lang === 'de' ? 'Bitte gültige Telefonnummer eingeben' : 'Please enter a valid phone number'); return; }
    setLoading(true); setError('');
    try {
      const result = await sendOTP({ phone, channel: 'sms' });
      const d = (result as any)?.data;
      if (d?.channel_used) setChannelUsed(d.channel_used);
      setVerifyChannel('phone');
      setStep('otp'); setCountdown(60); setOtp(['', '', '', '']);
    } catch (err: any) {
      const msg = err.message || '';
      if (msg.includes('Too many')) setError(lang === 'de' ? 'Zu viele Versuche. Bitte später erneut.' : 'Too many attempts. Try again later.');
      else setError(lang === 'de' ? 'SMS konnte nicht gesendet werden.' : 'Failed to send SMS.');
    } finally { setLoading(false); }
  };

  // ─── Email OTP (landline fallback) ───
  const handleSendEmailOTP = async () => {
    if (!email || !email.includes('@')) { setError(lang === 'de' ? 'Bitte gültige E-Mail eingeben' : 'Please enter a valid email'); return; }
    setLoading(true); setError('');
    try {
      await sendEmailCode({ email });
      setVerifyChannel('email');
      setChannelUsed('email');
      setStep('otp'); setCountdown(60); setOtp(['', '', '', '']);
    } catch (err: any) {
      setError(lang === 'de' ? 'E-Mail konnte nicht gesendet werden.' : 'Failed to send email.');
    } finally { setLoading(false); }
  };

  // ─── Verify code (works for both phone + email) ───
  const handleVerify = async (code: string) => {
    setLoading(true); setError('');
    try {
      let data: any;
      if (verifyChannel === 'email') {
        const result = await verifyEmailCode({ email, code, phone: phone || '' });
        data = result.data;
      } else {
        const result = await verifyOTP({ phone, code });
        data = result.data as any;
      }
      if (data?.success && data?.sessionToken) {
        // Merge phone + email into the profile object so downstream components
        // (AuthContext, AccountPage, CheckoutPage) all see them. Previously
        // profile was empty {} for new OTP users → phone/email never propagated.
        const mergedProfile = {
          ...(data.profile || {}),
          phone: data.phone || phone || (data.profile || {}).phone || '',
          email: data.email || email || (data.profile || {}).email || '',
        };
        localStorage.setItem('sushiki_session', JSON.stringify({
          sessionToken: data.sessionToken, uid: data.uid,
          phone: mergedProfile.phone,
          email: mergedProfile.email,
          isNewUser: data.isNewUser,
          profile: mergedProfile,
          verified_at: Date.now(),
        }));
        window.dispatchEvent(new Event('sushiki-auth-changed'));
        const profile = data.profile || {};
        if (data.isNewUser || !profile.name) navigate('/complete-profile', { state: { from: returnTo }, replace: true });
        else navigate(returnTo, { replace: true });
      } else {
        setError(lang === 'de' ? 'Verifizierung fehlgeschlagen' : 'Verification failed');
      }
    } catch (err: any) {
      const msg = err.message || '';
      if (msg.includes('expired')) setError(lang === 'de' ? 'Code abgelaufen.' : 'Code expired.');
      else if (msg.includes('Invalid')) setError(lang === 'de' ? 'Ungültiger Code.' : 'Invalid code.');
      else setError(lang === 'de' ? 'Verifizierung fehlgeschlagen' : 'Verification failed');
      setOtp(['', '', '', '']); otpRefs[0].current?.focus();
    } finally { setLoading(false); }
  };

  const handleOtpChange = (i: number, v: string) => {
    if (v.length > 1) v = v.slice(-1);
    if (v && !/^\d$/.test(v)) return;
    const n = [...otp]; n[i] = v; setOtp(n);
    if (v && i < 3) otpRefs[i + 1].current?.focus();
    if (v && i === 3 && n.every(d => d !== '')) handleVerify(n.join(''));
  };
  const handleOtpKey = (i: number, e: React.KeyboardEvent) => { if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs[i - 1].current?.focus(); };
  const handleOtpPaste = (e: React.ClipboardEvent) => { e.preventDefault(); const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4); if (p.length === 4) { setOtp(p.split('')); otpRefs[3].current?.focus(); handleVerify(p); } };

  const handleResend = () => {
    if (countdown > 0) return;
    if (verifyChannel === 'email') handleSendEmailOTP();
    else handleSendPhoneOTP();
  };

  return (
    <div className="auth-page-v2">
      {/* Brand hero (desktop only) */}
      <aside
        className="auth-hero"
        style={{
          ['--brand-hero-image' as any]: brand.id === 'donvito'
            ? 'radial-gradient(circle at 30% 20%, rgba(232,169,75,0.32), transparent 60%), radial-gradient(circle at 80% 80%, rgba(245,234,209,0.1), transparent 55%)'
            : 'radial-gradient(circle at 30% 20%, rgba(148,232,21,0.25), transparent 60%), radial-gradient(circle at 80% 80%, rgba(148,232,21,0.12), transparent 55%)',
        }}
      >
        <div className="brand-mark">
          <BrandLogo size={brand.id === 'donvito' ? 96 : 64} showTagline={false} />
        </div>
        <div>
          <h1 className="tag">
            {lang === 'de' ? (
              <>
                {brand.id === 'donvito' ? 'Authentisch italienisch.' : brand.tagline.de}
                <br />
                <span>{brand.id === 'donvito' ? 'Direkt aus dem Steinofen.' : 'Direkt aus der Küche.'}</span>
              </>
            ) : (
              <>
                {brand.id === 'donvito' ? 'Authentic Italian.' : brand.tagline.en}
                <br />
                <span>{brand.id === 'donvito' ? 'Straight from the stone oven.' : 'Straight from the kitchen.'}</span>
              </>
            )}
          </h1>
          <p className="sub">
            {brand.id === 'donvito'
              ? (lang === 'de'
                ? 'Melde dich an oder bestelle als Gast — in wenigen Minuten bei dir in Hürth.'
                : 'Sign in or order as a guest — delivered fresh to your door in Hürth.')
              : (lang === 'de'
                ? 'Melde dich an oder bestelle als Gast — handgerolltes Sushi direkt aus Hürth.'
                : 'Sign in or order as a guest — hand-rolled sushi straight from Hürth.')}
          </p>
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--brand-text-muted)' }}>
          {brand.id === 'donvito' ? 'Pizza Don Vito · Hürth' : 'SushiKi · Hürth'}
        </div>
      </aside>

      {/* Form side */}
      <div className="auth-form-col">
        <div className="auth-form-card">
          <div className="auth-topbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <button className="back-btn" onClick={() => navigate(-1)}>&#8592;</button>
            <div className="lang-toggle-landing">
              <button className={lang === 'de' ? 'active' : ''} onClick={() => setLang('de')}>DE</button>
              <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
            </div>
          </div>

          <div className="auth-container" style={{ padding: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ marginBottom: 20 }}>
              <BrandLogo size={56} />
            </div>

        {/* ─── Step: Phone or Email entry ─── */}
        {(step === 'phone' || step === 'email') && (
          <>
            <h2 style={{ color: '#fff', marginBottom: 4, fontSize: '1.3rem' }}>
              {lang === 'de' ? 'Willkommen' : 'Welcome'}
            </h2>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: 24, textAlign: 'center', maxWidth: 340 }}>
              {step === 'email'
                ? (lang === 'de'
                  ? 'Festnetznummer erkannt. Bitte geben Sie Ihre E-Mail ein — wir senden Ihnen einen Bestätigungscode.'
                  : 'Landline number detected. Please enter your email — we\'ll send you a verification code.')
                : (lang === 'de'
                  ? 'Geben Sie Ihre Telefonnummer ein. Wir senden den Code per WhatsApp – oder per SMS.'
                  : 'Enter your phone number. We\'ll send the code via WhatsApp — or SMS.')}
            </p>

            {step === 'phone' && (
              <div style={{ width: '100%', maxWidth: 340 }}>
                <label style={{ color: '#bbb', fontSize: '0.8rem', marginBottom: 6, display: 'block' }}>
                  {lang === 'de' ? 'Telefonnummer' : 'Phone Number'} <span style={{ color: '#ef5350' }}>*</span>
                </label>
                <PhoneInput
                  international defaultCountry="DE" value={phone} onChange={setPhone}
                  placeholder="176 12345678 / 0221 1234567"
                  style={{ '--PhoneInputCountryFlag-height': '1.2em' } as React.CSSProperties}
                />
                <style>{`
                  .PhoneInput { background: #1e2130; border: 1px solid #3a3d52; border-radius: 10px; padding: 10px 14px; display: flex; align-items: center; gap: 10px; }
                  .PhoneInput:focus-within { border-color: var(--brand-primary); }
                  .PhoneInputCountry { display: flex; align-items: center; gap: 4px; }
                  .PhoneInputCountryIcon { width: 24px; height: 18px; border-radius: 2px; overflow: hidden; }
                  .PhoneInputCountryIcon--border { box-shadow: none; background: transparent; }
                  .PhoneInputCountrySelectArrow { border-color: #9ca3af; opacity: 0.6; margin-left: 4px; }
                  .PhoneInputCountrySelect { background: #1a1d2e; color: #fff; }
                  .PhoneInputCountrySelect option { background: #1a1d2e; color: #fff; }
                  .PhoneInputInput { background: transparent !important; border: none !important; color: #fff !important; font-size: 1.1rem !important; outline: none !important; flex: 1; padding: 4px 0 !important; letter-spacing: 0.5px; }
                  .PhoneInputInput::placeholder { color: #555 !important; }
                `}</style>

                {/* Landline hint */}
                {landlineDetected && (
                  <div style={{
                    marginTop: 10, padding: '10px 14px', borderRadius: 10,
                    background: 'rgba(255,193,7,0.08)', border: '1px solid rgba(255,193,7,0.25)',
                    fontSize: '0.78rem', color: '#FFC107', lineHeight: 1.5,
                  }}>
                    📞 {lang === 'de'
                      ? 'Dies ist eine Festnetznummer. SMS/WhatsApp nicht möglich.'
                      : 'This is a landline number. SMS/WhatsApp not possible.'}
                    <button onClick={() => setStep('email')} style={{
                      display: 'block', marginTop: 8, background: '#FFC107', color: '#1a1d2e',
                      border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700,
                      fontSize: '0.82rem', cursor: 'pointer', width: '100%',
                    }}>
                      ✉️ {lang === 'de' ? 'Stattdessen per E-Mail verifizieren' : 'Verify via email instead'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {step === 'email' && (
              <div style={{ width: '100%', maxWidth: 340 }}>
                <label style={{ color: '#bbb', fontSize: '0.8rem', marginBottom: 6, display: 'block' }}>
                  E-Mail <span style={{ color: '#ef5350' }}>*</span>
                </label>
                <input type="email" value={email} placeholder="name@example.com"
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    width: '100%', padding: '14px 16px', borderRadius: 10,
                    border: '1px solid #3a3d52', background: '#1e2130', color: '#fff',
                    fontSize: '1.05rem', outline: 'none', boxSizing: 'border-box',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--brand-primary)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#3a3d52'; }}
                />
                {/* Back to phone option */}
                <button onClick={() => setStep('phone')} style={{
                  background: 'transparent', border: 'none', color: '#9ca3af',
                  fontSize: '0.78rem', cursor: 'pointer', marginTop: 10, textDecoration: 'underline',
                }}>
                  {lang === 'de' ? '← Zurück zur Telefonnummer' : '← Back to phone number'}
                </button>
              </div>
            )}

            {error && <p style={{ color: '#ef5350', fontSize: '0.8rem', marginTop: 8 }}>{error}</p>}

            <button
              onClick={step === 'email' ? handleSendEmailOTP : handleSendPhoneOTP}
              disabled={loading || (step === 'phone' ? (!phone || phone.length < 8) : !email.includes('@'))}
              style={{
                width: '100%', maxWidth: 340, padding: '14px 0', borderRadius: 10,
                border: 'none', background: loading ? '#555' : 'var(--brand-primary)', color: '#1a1d2e',
                fontSize: '1rem', fontWeight: 700, cursor: loading ? 'wait' : 'pointer',
                marginTop: 20,
                opacity: (step === 'phone' ? (!phone || phone.length < 8) : !email.includes('@')) ? 0.5 : 1,
              }}>
              {loading
                ? (lang === 'de' ? 'Wird gesendet...' : 'Sending...')
                : step === 'email'
                  ? (lang === 'de' ? 'Code per E-Mail senden' : 'Send code via email')
                  : (lang === 'de' ? 'Code senden' : 'Send code')}
            </button>

            {step === 'phone' && !landlineDetected && (
              <p style={{ color: '#666', fontSize: '0.75rem', marginTop: 10, textAlign: 'center' }}>
                {lang === 'de' ? '🟢 WhatsApp zuerst • 💬 SMS bei Bedarf' : '🟢 WhatsApp first • 💬 SMS if needed'}
              </p>
            )}

            {step === 'phone' && (
              <button onClick={() => { setStep('email'); setError(''); }}
                style={{
                  background: 'transparent', border: 'none',
                  color: '#9ca3af', fontSize: '0.8rem', cursor: 'pointer',
                  marginTop: 6, textDecoration: 'underline',
                }}>
                ✉️ {lang === 'de' ? 'Stattdessen mit E-Mail anmelden' : 'Sign in with email instead'}
              </button>
            )}

            <button onClick={() => navigate(returnTo, { replace: true })}
              style={{
                background: 'transparent', border: 'none', color: '#9ca3af',
                fontSize: '0.8rem', cursor: 'pointer', marginTop: 16, textDecoration: 'underline',
              }}>
              {lang === 'de' ? 'Ohne Anmeldung fortfahren' : 'Continue without sign in'}
            </button>
          </>
        )}

        {/* ─── Step: OTP entry (same for phone + email) ─── */}
        {step === 'otp' && (
          <>
            <h2 style={{ color: '#fff', marginBottom: 4, fontSize: '1.3rem' }}>
              {lang === 'de' ? 'Code eingeben' : 'Enter Code'}
            </h2>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: 8, textAlign: 'center' }}>
              {verifyChannel === 'email'
                ? (lang === 'de' ? 'Code per ✉️ E-Mail gesendet an' : 'Code sent via ✉️ email to')
                : (lang === 'de'
                  ? `Code per ${channelUsed === 'whatsapp' ? '🟢 WhatsApp' : '💬 SMS'} gesendet an`
                  : `Code sent via ${channelUsed === 'whatsapp' ? '🟢 WhatsApp' : '💬 SMS'} to`)}
            </p>
            <p style={{ color: 'var(--brand-primary)', fontWeight: 600, fontSize: '1rem', marginBottom: 4 }}>
              {verifyChannel === 'email' ? email : phone}
            </p>
            <button onClick={() => { setStep(verifyChannel === 'email' ? 'email' : 'phone'); setError(''); }}
              style={{
                background: 'transparent', border: 'none', color: 'var(--brand-primary)',
                fontSize: '0.78rem', cursor: 'pointer', marginBottom: 20, textDecoration: 'underline',
              }}>
              {verifyChannel === 'email'
                ? (lang === 'de' ? 'E-Mail ändern' : 'Change email')
                : (lang === 'de' ? 'Nummer ändern' : 'Change number')}
            </button>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
              {otp.map((digit, i) => (
                <input
                  key={i} ref={otpRefs[i]} type="text" inputMode="numeric" maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKey(i, e)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  style={{
                    width: 56, height: 64, textAlign: 'center', fontSize: '1.8rem',
                    fontWeight: 700, borderRadius: 12,
                    border: `2px solid ${digit ? 'var(--brand-primary)' : '#3a3d52'}`,
                    background: '#1e2130', color: '#fff', outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--brand-primary)'; }}
                  onBlur={(e) => { if (!digit) e.target.style.borderColor = '#3a3d52'; }}
                />
              ))}
            </div>

            {error && <p style={{ color: '#ef5350', fontSize: '0.8rem', marginTop: 4, marginBottom: 8 }}>{error}</p>}
            {loading && <p style={{ color: 'var(--brand-primary)', fontSize: '0.85rem', marginBottom: 8 }}>{lang === 'de' ? 'Wird überprüft...' : 'Verifying...'}</p>}

            <button onClick={handleResend} disabled={countdown > 0}
              style={{
                background: 'transparent', border: 'none',
                color: countdown > 0 ? '#666' : 'var(--brand-primary)',
                fontSize: '0.8rem', cursor: countdown > 0 ? 'default' : 'pointer', marginTop: 8,
              }}>
              {countdown > 0
                ? (lang === 'de' ? `Erneut senden in ${countdown}s` : `Resend in ${countdown}s`)
                : (lang === 'de' ? 'Code erneut senden' : 'Resend code')}
            </button>
          </>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
